
import { ChatProviderType } from '../../../../domain/chat/models/ChatProviderType';
import { IChatSessionWriteService, CreateChatSessionRequest, UpdateChatSessionRequest } from '../../../chat/interfaces/IChatSessionWriteService';
import { ChatSessionLoadResult } from '../../../chat/interfaces/IChatSessionsService';
import { BaseService } from '../../../http/BaseHttpService';
import { ProGPTCreateChatSessionResponseDto } from '../dtos/responses/ProGPTCreateChatSessionResponseDto';
import { ProGPTResultDto } from '../dtos/responses/ProGPTResultDto';
import { ProGPTChatSessionDto } from '../dtos/responses/ProGPTChatSessionDto';
import { mapProGPTChatSessionDtoToModel } from '../mappers/ProGPTChatSessionMapper';
import { buildProGPTUrl } from '../../../../features/utils/buildProGPTUrl';
import { ProGPTUploadBulkPromptResponseDto } from '../dtos/responses/ProGPTUploadBulkPromptResponseDto';

export class ProGPTChatSessionWriteService extends BaseService implements IChatSessionWriteService {
    public readonly providerType = ChatProviderType.ProGPT;

    constructor(private readonly baseUrl: string) {
        super();
    }

    public async createChatAsync(request: CreateChatSessionRequest, accessToken: string): Promise<ChatSessionLoadResult> {
        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTCreateChatSessionResponseDto>>(

            {
                url: buildProGPTUrl(this.baseUrl, 'ChatSession'),
                method: 'POST',
                body: {
                    title: request.title,
                },
            },
            accessToken,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to create ProGPT chat session.');
        }

        return {
            session: mapProGPTChatSessionDtoToModel(response.value.chatSession),
            documents: [],
        };
    }

    public async updateChatAsync(
        chatId: string,
        request: UpdateChatSessionRequest,
        accessToken: string,
    ): Promise<ChatSessionLoadResult> {
        if (request.providerType !== ChatProviderType.ProGPT) {
            throw new Error('ProGPTChatSessionWriteService received a non-ProGPT update request.');
        }

        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTChatSessionDto>>(

            {
                url: buildProGPTUrl(this.baseUrl, `ChatSession/${chatId}`),
                method: 'PATCH',
                body: {
                    chatId: request.chatId,
                    ...(request.title !== undefined && { title: request.title }),
                    ...(request.persona !== undefined && { persona: request.persona }),
                    ...(request.isTitleGenerated !== undefined && { isTitleGenerated: request.isTitleGenerated }),
                },
            },
            accessToken,
        );

        if (!response.isSuccess) {
            throw new Error(response.errors?.join(', ') || 'Failed to update ProGPT chat session.');
        }

        return {
            session: mapProGPTChatSessionDtoToModel(response.value),
            documents: [],
        };
    }

    public async uploadBulkPromptsAsync(chatId: string, file: File, accessToken: string): Promise<string> {
        const formData = new FormData();
        formData.append('ChatId', chatId);
        formData.append('FileName', file.name);
        formData.append('PromptFile', file);
        const response = await this.getResponseAsync<ProGPTResultDto<ProGPTUploadBulkPromptResponseDto>>(
            {
                url: buildProGPTUrl(this.baseUrl, `ChatSession/${chatId}/bulkPrompts`),
                method: 'POST',
                body: formData,
            },
            accessToken,
        );

        if (!response.isSuccess || !response.value?.fileName) {
            throw new Error(response.errors?.join(', ') || 'Failed to upload bulk prompts.');
        }

        return response.value.fileName;
    }

    public async downloadBulkPromptsAsync(chatId: string, fileName: string, accessToken: string): Promise<File> {
        const response = await this.getBlobResponseAsync<Blob>(
            {
                url: buildProGPTUrl(this.baseUrl, `ChatSession/${chatId}/bulkPrompts/${encodeURIComponent(fileName)}`),
                method: 'GET',
            },
            accessToken,
        );

        return new File([response], fileName.split('_').slice(1).join('_') || fileName, {
            type: response.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
    }
}
